import i18n from '@assets/i18n/config';
import { GoalTypes } from '@generated/graphql';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import { AccountTypes, type CountryCodes } from '@onevesthq/ov-enums';
import countries from 'i18n-iso-countries';
import countriesEnglish from 'i18n-iso-countries/langs/en.json';
import countriesSpanish from 'i18n-iso-countries/langs/es.json';
import countriesFrench from 'i18n-iso-countries/langs/fr.json';
import { type CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import BuildWealthIcon from '../assets/images/goals/build-wealth.svg';
import BuyACarIcon from '../assets/images/goals/buy-a-car.svg';
import BuyAHouseIcon from '../assets/images/goals/buy-a-house.svg';
import EducationIcon from '../assets/images/goals/education.svg';
import MajorPurchaseIcon from '../assets/images/goals/major-purchase.svg';
import OtherIcon from '../assets/images/goals/other.svg';
import ParentalLeaveIcon from '../assets/images/goals/parental-leave.svg';
import RetirementIcon from '../assets/images/goals/retirement.svg';
import SafetyNetIcon from '../assets/images/goals/safety-net.svg';
import SomethingElseIcon from '../assets/images/goals/something-else.svg';
import StartABusinessIcon from '../assets/images/goals/start-a-business.svg';
import VacationIcon from '../assets/images/goals/vacation.svg';
import WeddingIcon from '../assets/images/goals/wedding.svg';
import { useLocalization } from '../hooks/useLocalization';

export type Country = {
  name: string;
  code: string;
};

// i18n: When adding a new language to LanguageCodes (@onevesthq/ov-enums),
// also import its locale bundle from `i18n-iso-countries/langs/{code}.json`
// and register it below. Otherwise country-name dropdowns will fall back to English.
countries.registerLocale(countriesEnglish);
countries.registerLocale(countriesFrench);
countries.registerLocale(countriesSpanish);

const buildCountryListForLocale = (locale: string): Country[] => {
  const names = countries.getNames(locale, { select: 'official' });
  if (!names || Object.keys(names).length === 0) {
    // Fallback to English if locale not registered
    return buildCountryListForLocale('en');
  }
  let list = Object.entries(names).map(([code, name]) => ({ name, code }));

  // Taiwan: remove ", Province of China"
  list = list.map((country) =>
    country.code === 'TW'
      ? {
          code: country.code,
          name: country.name.replace(', Province of China', ''),
        }
      : country,
  );

  // Exclude Kosovo (XK) - TODO: Remove once we properly add Kosovo to ov enums
  list = list.filter((country) => country.code !== 'XK');

  // Pin Canada and US at top
  const us = list.find((c) => c.code === 'US');
  const ca = list.find((c) => c.code === 'CA');
  const rest = list.filter((c) => c.code !== 'US' && c.code !== 'CA');
  if (ca && us) {
    list = [ca, us, ...rest];
  }

  return list;
};

export const getCountryList = (locale: string): Country[] =>
  buildCountryListForLocale(locale);

export const useCountryList = (): Country[] => {
  const { language } = useLocalization();
  return getCountryList(language);
};

export const getCountryName = (code: string, locale: string): string => {
  const list = getCountryList(locale);
  const matching = list.find((c) => c.code === code);
  return matching ? matching.name : '';
};

export const getCallingCodesForApplicableCountries = (
  applicableCountries: CountryCodes[],
): Record<CountryCodes, string> => {
  const result: Record<CountryCodes, string> = {} as Record<
    CountryCodes,
    string
  >;

  applicableCountries.forEach((countryCode) => {
    try {
      const callingCode = getCountryCallingCode(countryCode as CountryCode);
      result[countryCode] = `+${callingCode}`;
    } catch {
      result[countryCode] = '';
    }
  });

  return result;
};

export const provinces = [
  {
    key: 'AB',
    value: 'Alberta',
  },
  {
    key: 'BC',
    value: 'British Columbia',
  },
  {
    key: 'MB',
    value: 'Manitoba',
  },
  {
    key: 'NB',
    value: 'New Brunswick',
  },
  {
    key: 'NL',
    value: 'Newfoundland and Labrador',
  },
  {
    key: 'NS',
    value: 'Nova Scotia',
  },
  {
    key: 'ON',
    value: 'Ontario',
  },
  {
    key: 'PE',
    value: 'Prince Edward Island',
  },
  {
    key: 'SK',
    value: 'Saskatchewan',
  },
  {
    key: 'QC',
    value: 'Quebec',
  },
  {
    key: 'YT',
    value: 'Yukon',
  },
  {
    key: 'NT',
    value: 'Northwest Territories',
  },
  {
    key: 'NU',
    value: 'Nunavut',
  },
  {
    key: 'FEDERAL',
    value: 'Federal',
  },
];

export const applicableProvinces = [
  {
    accountType: AccountTypes.LIRA,
    availability: ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'ON', 'QC', 'SK'],
  },
  {
    accountType: AccountTypes.PRIF,
    availability: ['MB', 'SK'],
  },
  {
    accountType: AccountTypes.LRIF,
    availability: ['NL'],
  },
  {
    accountType: AccountTypes.LRSP,
    availability: ['FEDERAL'],
  },
  {
    accountType: AccountTypes.RLSP,
    availability: ['FEDERAL'],
  },
  {
    accountType: AccountTypes.RLIF,
    availability: ['FEDERAL'],
  },
  {
    accountType: AccountTypes.LIF,
    availability: ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'ON', 'QC', 'FEDERAL'],
  },
  {
    accountType: AccountTypes.RRIF,
    availability: ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'ON', 'QC', 'FEDERAL'],
  },
  {
    accountType: AccountTypes.RIF_SPOUSAL,
    availability: ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'ON', 'QC', 'FEDERAL'],
  },
];

export type GoalTypeAttributes = {
  key: GoalTypes;
  iconPath: string;
  defaultTargetAmountCents?: number;
  secondaryGoalTypes?: GoalTypeAttributes[];
};

export const goalTypes: GoalTypeAttributes[] = [
  {
    key: GoalTypes.BuildWealth,
    iconPath: BuildWealthIcon,
    defaultTargetAmountCents: 5000000,
  },
  {
    key: GoalTypes.Retirement,
    iconPath: RetirementIcon,
    defaultTargetAmountCents: 100000000,
  },
  {
    key: GoalTypes.MajorPurchase,
    iconPath: MajorPurchaseIcon,
    secondaryGoalTypes: [
      {
        key: GoalTypes.Car,
        iconPath: BuyACarIcon,
        defaultTargetAmountCents: 5000000,
      },
      {
        key: GoalTypes.House,
        iconPath: BuyAHouseIcon,
        defaultTargetAmountCents: 10000000,
      },
      {
        key: GoalTypes.Education,
        iconPath: EducationIcon,
        defaultTargetAmountCents: 3500000,
      },
      {
        key: GoalTypes.Vacation,
        iconPath: VacationIcon,
        defaultTargetAmountCents: 400000,
      },
      {
        key: GoalTypes.StartABusiness,
        iconPath: StartABusinessIcon,
        defaultTargetAmountCents: 2500000,
      },
      {
        key: GoalTypes.Wedding,
        iconPath: WeddingIcon,
        defaultTargetAmountCents: 2500000,
      },
      {
        key: GoalTypes.ParentalLeave,
        iconPath: ParentalLeaveIcon,
        defaultTargetAmountCents: 300000,
      },
      {
        key: GoalTypes.MajorPurchase,
        iconPath: SomethingElseIcon,
        defaultTargetAmountCents: 1000000,
      },
    ],
  },
  {
    key: GoalTypes.SafetyNet,
    iconPath: SafetyNetIcon,
    defaultTargetAmountCents: 500000,
  },
  {
    key: GoalTypes.Other,
    iconPath: OtherIcon,
    defaultTargetAmountCents: 1000000,
  },
];

export const getGoalTitle = (goal: Partial<Goal>) =>
  // Yield a title in the following order: goalName > goalTitle > "Goal"
  goal?.name
    ? goal.name
    : goal?.type
      ? i18n.t(`client:goalType.${goal?.type}`)
      : i18n.t('workflowCompletions:goals.goal');
